// NPM Imports
import { pick } from "lodash";

// Internal Imports
import * as DealTable from "./DealTable";
import * as Pricebook from "./Pricebook";
import { GRID } from "../constants";
import { generateBasedOnType, generateProps } from "./methods";

// Destructuring
const { COLUMN_TYPES, DEALTABLE_COLUMNS, GRID_PAGE_TYPE, PRICING_METHODS } =
  GRID;

/**
 * * Resturcture backend API JSON to support agGrid column syntax
 * @param {object} body - Contains the data of Column API response.
 * @param {string} type - Refers to the page, say "DealTable/Products/Pricebook".
 */
export const formatColumnData = (body, type, currencySymbol = "$") => {
  const apiRequiredFields = []; // Stores FLDXXX of direct columns which Backend API needs
  const columnDefs = [
    {
      editable: false,
      headerName: "#",
      hidden: false,
      lockPosition: true,
      maxWidth: 80,
      minWidth: 50,
      pinned: "left",
      position: 0,
      sortable: true,
      suppressMenu: true,
      valueGetter: "Number(node.rowIndex) + 1",
    },
  ]; // Generate columns definitions, behaviour of each columns will be based on this.
  const customColumnDefs = []; // Stores custom columns FLDXXX, (i.e) in Pricebook, custom columns can be created by users.
  const fieldMapIndex = {}; // Store the filed name map => {sku: FLDxWnEYKHlO4zM}
  const linkedTables = []; // Stores TBLXXX & FLDXXX's of the columns which are linked from different tables.
  const props = {}; // Stores the dropdown options of the singleSelect/multiSelect columns.
  const tblId = body.VIW.tableId; // Referring to Dealtable ID.
  const { columnOrder } = body.VIW; // Order in which the columns has to be displayed.
  let derivedColumn = {}; // Stores the linked table id and which Field(FLDXX) has to be picked

  // Now loop through each column and generate JSON required by agGrid
  body.VIW.columns.forEach(item => {
    const columnName = `col-${item.name.split("_").join("-")}`; // Need column name as classname in each cell for selinium to identify
    const obj = {}; // This will hold the final properties of each column
    obj.columnId = item.id;
    obj.currencySymbol = currencySymbol;
    obj.editable = item.editable;
    obj.field = item.field.id;
    obj.headerName = item.label;
    obj.hidden = item.hidden;
    obj.hide = item.hidden || !item.visible;
    obj.name = item.name;
    obj.position = (columnOrder && columnOrder.indexOf(obj.name) + 1) || 100;
    obj.props = item.field.props;
    obj.required = item.field.required;
    obj.resizable = true;
    obj.sortable = true;
    obj.standard = item.field.standard;
    obj.suppressMenu = false;
    obj.type = COLUMN_TYPES[item.field.type];
    obj.columnType = item.type;
    obj.visible = item.visible;
    obj.width = 100;
    obj.enableRowGroup = true;
    obj.suppressSizeToFit = true;
    obj.wrapText = true;
    obj.autoHeight = true;
    obj.cellStyle = { "white-space": "normal", "line-height": 1.5 };
    obj.menuTabs = ["generalMenuTab", "gibberishMenuTab"];

    /**
     * Since the column names are dynamic and sent as FLDXXX from backend it will
     * create a problem when we need to target a specific column, he we create a map
     * which points the filed name to its respective FLDXXX key => {sku: FLDxWnEYKHlO4zM}
     */
    fieldMapIndex[item.name] = item.field.id;

    /** By default the column name should be added to the "cellClass" property.
     * Backend can decide if the cell can be editable or not, for this we just
     * add our custom "rs-cell-editable" classname to the cellClass property.
     */
    obj.cellClass = item.editable
      ? [columnName, "rs-cell-editable"]
      : [columnName];

    /** Now we need to seperate the columns belonging to the same table and linked table.
     * "apiRequiredFields" => Hold the column FLDXXX belonging to same table
     * "linkedTables" => Holds linked table ID and its FLDXXX as arrayOfObjects
     */
    if (item.field.tblId === tblId) {
      apiRequiredFields.push(item.field.id);
    } else {
      const isTblExists = linkedTables.find(
        tbl => tbl.tblId === item.field.tblId,
      );
      if (isTblExists) {
        isTblExists.ids.push(item.field.id); // If the Table is already created then push the field(FLDXXX) into "ids" key
      } else {
        linkedTables.push({
          tblId: item.field.tblId,
          ids: [item.field.id],
        }); // Create an array to store linked table id's & field id's
      }
    }

    // Filter out the derived columns, needed in dealtable
    if (item.type === "DerivedColumn") {
      derivedColumn = {
        sourceField: item.field.props.sourceField,
        sourceTable: item.props.sourceTable,
      };
    }

    /** A column can be of different types like input, dropdown, date picker, etc.
     * Since each column type behaviour is different, respective agGrid properies have to be added.
     */
    generateBasedOnType(item, obj);

    /** When creating a new entry in PRICEBOOK/PRODUCTS page forms, the dropdown options
     * are extracted from this column API and will to be displayed as select input type in the form.
     */
    generateProps(item, props, type);

    /** Add addition properties to the column definition based on the column, here the column name is
     * used to identify the column. Since this is not fixed, hence seperate files are created based on
     * page to handle this action.
     */

    switch (type) {
      case GRID_PAGE_TYPE.DEALTABLE:
        DealTable.generateBasedOnName(obj.name.toLowerCase(), obj);
        break;
      case GRID_PAGE_TYPE.PRICELIST:
        Pricebook.generateBasedOnName(obj.name.toLowerCase(), obj);

        // Page level grid changes are added below
        obj.editable = false;
        obj.cellClass = [columnName];

        // Create columnDefs for custom columns added by the user
        if (!obj.standard) {
          const newObj = {};
          newObj.colId = obj.columnId;
          newObj.field = obj.field;
          newObj.name = obj.name;
          newObj.label = obj.headerName;
          newObj.props = obj.props;
          newObj.type = obj.type;
          newObj.columnType = obj.columnType;
          customColumnDefs.push(newObj);
        }
        break;
      case GRID_PAGE_TYPE.PRODUCTS:
        // Page level grid changes are added below
        obj.editable = false;
        obj.cellClass = [columnName];
        if (!obj.standard) {
          const newObj = {};
          newObj.colId = obj.columnId;
          newObj.field = obj.field;
          newObj.name = obj.name;
          newObj.label = obj.headerName;
          newObj.props = obj.props;
          newObj.type = obj.type;
          customColumnDefs.push(newObj);
        }
        break;
      default:
        break;
    }

    // Push all columnDefs data inside an ARRAY
    columnDefs.push(obj);
  });

  /** In the above forEach func, the loop is done based on the columns provided by the API,
   * now when displaying, the columns have to be displayed in a specific order, to achieve this
   * we have used "position" key which will set a number like "6,2,etc" which defines the order position.
   * Now we need to sort them in ascending order.
   */
  columnDefs.sort((a, b) => a.position - b.position);

  /** The custom key "id" is used in the rows to diff between the saved row or newly added row,
   * basically decides PUT/POST CRUD operation. The "apiRequiredFields" is used to removed unwanted
   * row data during the time of save, hence we add this custom key here.
   */
  apiRequiredFields.push("id");

  /** Now all these data are concated into a single object and returned back to DealTableAction file
   */
  const returnObj = {
    columnDefs,
    customColumnDefs,
    metaData: {
      apiRequiredFields,
      derivedColumn,
      fieldMapIndex,
      linkedTables,
      masterTblID: body.VIW.masterTblID || null,
      tableId: body.VIW.tableId,
      tblViewId: body.VIW.id,
    },
    props,
  };
  return returnObj;
};

/**
 * The row API have data related to same table and linked table, we need to combine them together
 * the generate a new JOSN which the agGrid expects, say => {FLDxWnEYKHlO4zM: "Recurring"}
 * @param {arrayOfObject} rows - Contains the data of Rows from API associated to the dealtable id.
 * @param {object} metaData - Contains data which helps to perform action in ag-grid, refer above "formatColumnData" func for more info.
 * @param {string} page - Refers to the page, say "DealTable/Products/Pricebook".
 */
export const formatRowData = (rows, metaData, page) => {
  const { linkedTables, fieldMapIndex, derivedColumn } = metaData;
  const { sourceTable, sourceField } = derivedColumn; // Holds Tbl id & field id to be looked up in the linked records.
  const addedCatalogIds = []; // Stores the catalog product id's which are already added to dealtable => [5,16]
  const bundleDataFld = fieldMapIndex[DEALTABLE_COLUMNS.BUNDLE_QUANTITY]; // FLDXXX holding the products inside the bundle [{}].
  const catalogProductId = fieldMapIndex[DEALTABLE_COLUMNS.PRICELIST_ITEM]; // FLDXXX holding the product id.
  const rowData = []; // Stores the final row data
  rows.forEach((item, index) => {
    const linkedTableFields = {}; // Pickout the FLDXXX keys from the linked record tables
    linkedTables.forEach(tbl => {
      const linkedRows = item.linkedRows.find(row => row.tblId === tbl.tblId);
      if (linkedRows) {
        linkedRows.rows.forEach(row => {
          Object.assign(linkedTableFields, pick(row.colValues, tbl.ids));
        });
      }
    });
    /**
     * "item.colValues" => Holds the row FLDXXX keys from the API
     * "linkedTableFields" => Holds the FLDXXX keys from the linked record tables
     * Additionally the "id" & "index" keys are added & combined into a single obj
     */
    const mergedObj = Object.assign({}, item.colValues, linkedTableFields, {
      id: item.rowId,
      index: item.colValues[catalogProductId] || index, // Holds the product id, here index is used for deltaRowDataMode
    });

    // DEALTABLE page specific value mutatation
    if (page === GRID_PAGE_TYPE.DEALTABLE) {
      /**
       * Pricing method column holds OBJECT as data type instead of INTEGER, and this obj data is available inside
       * a linked record table. Hence the below method is used to pick out that particular FLDXXX key.
       */
      mergedObj.metaData = item.linkedRows.find(
        column => column.tblId === sourceTable,
      ).rows[0].colValues[sourceField];

      /**
       * In case of pricing method being bundle, the person efforts entered in dealtable by the user should be retained & should
       * not show what entered in pricing page. For that a hidden column is used to get the updated bunlde values.
       */

      if (
        mergedObj.metaData &&
        mergedObj.metaData.type === PRICING_METHODS.BUNDLE_PRICING &&
        mergedObj[bundleDataFld]
      ) {
        mergedObj.metaData = mergedObj[bundleDataFld];
      }

      /** Products id's are pushed into an array, this will make the UI to display that the product is already added */
      const productId = item.colValues[catalogProductId];
      if (productId) addedCatalogIds.push(productId);
    }
    rowData.push(mergedObj);
  });
  return {
    rowData,
    addedCatalogIds,
  };
};
